<script setup lang="ts">
import Translator from "@/utils/common/Translator.ts";
import NamaIcon from "@/components/icons/NamaIcon.vue";
import WidgetContainer from "@/components/EditView/widgets/WidgetContainer.vue";
import {useQuestionList} from "./useQuestionList.ts";
import QuestionListPanel from "./QuestionListPanel.vue";
import {computed, nextTick, onBeforeUnmount, onMounted, ref, watch} from "vue";
import {WidgetsPresenter} from "@/utils/presenters/interfaces.ts";
import {QInput} from "quasar";
import {storeToRefs} from "pinia";
import {useNamaFlagsStore} from "@/stores/nama-flags-store.ts";
import QuestionField = namaTypes.QuestionField;

const props = withDefaults(defineProps<{
  presenter: WidgetsPresenter, questionField: QuestionField,
  doNotApplyThemeLayouts: boolean, mode?: 'list' | 'text'
}>(), {mode: 'list'});
const {isUltra} = storeToRefs(useNamaFlagsStore());
const {
  selectedDetails, selectedRowsNumber, getDetailText, removeDetail,
  searchText, isLoading, displayedItems, fieldTitle,
  isItemSelected, allSelected, someSelected,
  toggleAll, toggleItem, onPlusClick,
  pasteAndSelect, copySelected,
  selectionAsText, setSelectionFromText,
} = useQuestionList(props.presenter, props.questionField);

const textValue = ref('');

function onAvatarClick(e: Event) {
  if (props.mode === 'text')
    return;
  onPlusClick(e);
}

function onMenuShow() {
  if (props.mode === 'text')
    textValue.value = selectionAsText();
}

function onMenuHide() {
  if (props.mode === 'text')
    setSelectionFromText(textValue.value);
}

const selectAllState = computed<boolean | null>(() => allSelected.value ? true : someSelected.value ? null : false);

const CHIP_GAP = 4;
const CHIPS_ROW_MARGIN = 28;
const outerRef = ref<HTMLElement | null>(null);
const avatarRef = ref<{ $el: HTMLElement } | null>(null);
const measureRowRef = ref<HTMLElement | null>(null);
const visibleCount = ref<number>(Number.MAX_SAFE_INTEGER);
const hiddenChipsCount = computed(() => Math.max(0, selectedRowsNumber.value - visibleCount.value));

function availableChipsWidth(): number {
  const control = outerRef.value?.closest('.q-field__control') as HTMLElement | null;
  if (!control) return 0;
  const avatarWidth = avatarRef.value?.$el?.offsetWidth ?? 0;
  return control.clientWidth - avatarWidth - CHIPS_ROW_MARGIN;
}

function countChipsFitting(limit: number): number {
  const chips = Array.from(measureRowRef.value?.children ?? []) as HTMLElement[];
  let used = 0;
  let count = 0;
  for (let i = 0; i < chips.length; i++) {
    const add = (i === 0 ? 0 : CHIP_GAP) + chips[i].offsetWidth;
    if (used + add > limit) break;
    used += add;
    count++;
  }
  return count;
}

function recomputeVisibleChips() {
  const total = selectedRowsNumber.value;
  const available = availableChipsWidth();
  if (available <= 0) {
    visibleCount.value = total;
    return;
  }
  visibleCount.value = Math.min(countChipsFitting(available), total);
}

let resizeObserver: ResizeObserver | undefined;
onMounted(() => {
  resizeObserver = new ResizeObserver(() => recomputeVisibleChips());
  const control = outerRef.value?.closest('.q-field__control');
  if (control) resizeObserver.observe(control);
  nextTick(recomputeVisibleChips);
});
onBeforeUnmount(() => resizeObserver?.disconnect());
watch(selectedRowsNumber, () => nextTick(recomputeVisibleChips));
watch(isUltra, () => nextTick(recomputeVisibleChips));
</script>

<template>
  <WidgetContainer
    :field="questionField"
    :entityType="presenter.entityType"
    :isFieldDisabled="false"
    :isFieldRequired="questionField.required"
    :doNotApplyThemeLayouts="doNotApplyThemeLayouts"
    :isInsideComposite="false">
    <template #icon>
      <NamaIcon :block="questionField" :presenter="presenter" class="tw-size-[22px] tw-text-[22px]"/>
    </template>
    <template v-slot:widget>
      <q-input outlined dense hide-bottom-space
               :id="questionField.id"
               autocomplete="off"
               readonly
               class="nama-customize-dense-input nama-focused-input nama-field-question-list nama-field-input"
               input-class="nama-input-class"
               :model-value="''">
        <template v-slot:prepend>
          <div ref="outerRef"
               class="tw-flex tw-flex-wrap tw-gap-x-2 tw-gap-y-1 tw-p-2 compact:tw-p-1 ultra:tw-p-0.5 tw-items-center tw-justify-between tw-w-full">
            <div class="tw-flex tw-items-center tw-flex-wrap tw-gap-1 tw-p-0 tw-min-w-0 tw-overflow-hidden">
              <template v-for="(detail, index) in selectedDetails" :key="detail.id">
                <q-chip
                  v-if="index < visibleCount"
                  removable
                  :dense="isUltra"
                  size="sm"
                  class="tw-m-0 tw-shrink-0"
                  @remove="removeDetail(detail)">
                  <span class="tw-text-xs" v-text="getDetailText(detail)"/>
                </q-chip>
              </template>
            </div>
            <div ref="measureRowRef"
                 aria-hidden="true"
                 class="tw-absolute tw-invisible tw-pointer-events-none tw-flex tw-items-center tw-flex-nowrap tw-gap-1"
                 style="left: -9999px; top: -9999px;">
              <q-chip
                v-for="detail in selectedDetails"
                :key="'measure-' + detail.id"
                removable
                :dense="isUltra"
                size="sm"
                class="tw-m-0 tw-shrink-0">
                <span class="tw-text-xs" v-text="getDetailText(detail)"/>
              </q-chip>
            </div>
            <q-avatar ref="avatarRef" square @click.stop.prevent="onAvatarClick"
                      :title="Translator.translateId('select')"
                      class="tw-bg-primary tw-flex tw-flex-row tw-gap-0.5 hover:tw-bg-namaGray !tw-rounded dark:hover:tw-bg-namaGray !tw-w-fit tw-px-1 !tw-h-6 compact:!tw-h-[19px] ultra:!tw-h-4 tw-cursor-pointer tw-shrink-0">
              <span v-if="hiddenChipsCount"
                    class="tw-text-sm tw-text-white tw-font-semibold tw-leading-none tw-tabular-nums" v-text="hiddenChipsCount"/>
              <q-icon name="add" size="16px" color="white" class="tw-transition-transform group-hover:tw-rotate-90"/>
              <q-menu @click.stop anchor="bottom end" self="top end" @show="onMenuShow" @hide="onMenuHide"
                      class="tw-min-w-[260px] !tw-max-h-[45vh] tw-rounded-md tw-shadow-lg tw-bg-namaBackground-bright-light dark:tw-bg-namaBackground-bright-dark">
                <div v-if="mode === 'text'" class="tw-p-2 tw-min-w-[260px] tw-max-h-[300px]">
                  <q-input
                    outlined dense hide-bottom-space
                    autofocus
                    :hint="Translator.translateId('inValuesTextareaHint')"
                    type="textarea"
                    autocomplete="off"
                    class="nama-field-input nama-customize-dense-input nama-textarea-dense nama-focused-input control-big-text-microphone-opacity tw-overflow-hidden"
                    input-class="nama-input-class tw-h-16 tw-bg-transparent tw-font-normal tw-text-base ultra:tw-text-sm dark:tw-text-white focus:tw-ring-transparent focus:tw-ring-0 tw-px-1"
                    v-model="textValue"/>
                </div>
                <QuestionListPanel
                  v-else
                  v-model:searchText="searchText"
                  :items="displayedItems"
                  :is-loading="isLoading"
                  :select-all-state="selectAllState"
                  :is-item-selected="isItemSelected"
                  :field-title="fieldTitle"
                  :selected-rows-number="selectedRowsNumber"
                  @toggle-all="toggleAll"
                  @toggle-item="toggleItem"
                  @paste-text="pasteAndSelect"
                  @copy-selected="copySelected"/>
              </q-menu>
            </q-avatar>
          </div>
        </template>
      </q-input>
    </template>
  </WidgetContainer>
</template>
